import React from "react"
import PropTypes from "prop-types"
import { Row, Col, Flex, Card, Divider, Typography, Tooltip } from "antd"
import SummaryKeyValue from "ui/Booking/Details/Body/PaymentSummary/SummaryKeyValue"
import useSummaryDetails from "ui/Booking/Details/Body/PaymentSummary/hooks/useSummaryDetails"
import { numberToCurrency } from "helpers/string"
import StatusTag from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/StatusTag"
import { displayPaymentStatus } from "ui/Booking/Details/utils"
import moment from "moment"
import styles from "../../index.module.css"
import { Icon } from "ui/Icon"

const { Text } = Typography

const PaymentCard = React.memo(({ paymentStatus, paymentSummary, paymentHistory, pricing }) => {
  const allDetails = useSummaryDetails(paymentSummary, pricing, { combineFees: true })
  const { total_price, remaining_balance, adjust_price_history } = paymentSummary || {}
  const hasPayments = paymentHistory?.length > 0

  const priceLines = allDetails.filter((d) => !d.combinedFee)
  const feeRow = allDetails.find((d) => d.combinedFee)

  return (
    <Card className={styles.mainCard} title="Payment">
      <Row gutter={[16]} style={{ alignItems: "stretch" }}>
        <Col lg={hasPayments ? 12 : 24}>
          <div className={styles.priceDetails}>
            <Flex align="center" gap={16} justify="space-between">
              <Text className="color-text-secondary-dark" strong>
                Price Details
              </Text>
              <StatusTag status={displayPaymentStatus(paymentStatus)} />
            </Flex>
            <div className="mt-l">
              <SummaryKeyValue details={priceLines} valueAlign="right" />
              {feeRow && (
                <Row gutter={[0, 0]}>
                  <Col xs={14} md={12}>
                    <Text>Tax &amp; Fees</Text>
                  </Col>
                  <Col xs={10} md={12} style={{ textAlign: "right" }}>
                    <Text>{numberToCurrency(feeRow.value)}</Text>
                  </Col>
                </Row>
              )}
            </div>
            {adjust_price_history?.map((item) => {
              const { price_difference, reason, id } = item || {}
              return (
                <Flex key={id || `${price_difference}-${reason}`} align="center" justify="space-between" className="mt-xs">
                  <Flex align="center" gap={4}>
                    <Text>Price Change</Text>
                    {reason && (
                      <Tooltip title={reason}>
                        <span style={{ display: "inline-flex" }}>
                          <Icon type="feather" name="Info" width={16} height={16} />
                        </span>
                      </Tooltip>
                    )}
                  </Flex>
                  <Text>
                    {price_difference > 0 ? "+" : ""}
                    {numberToCurrency(price_difference)}
                  </Text>
                </Flex>
              )
            })}
            <Divider className="my-xs" />
            <Flex align="center" justify="space-between">
              <Text strong={!hasPayments}>Total</Text>
              <Text strong={!hasPayments}>{numberToCurrency(total_price)}</Text>
            </Flex>
            {hasPayments && remaining_balance !== undefined && (
              <Flex align="center" justify="space-between" className="mt-xs">
                <Text strong>Remaining Balance</Text>
                <Text strong>{numberToCurrency(remaining_balance)}</Text>
              </Flex>
            )}
          </div>
        </Col>
        {hasPayments && (
          <Col lg={12} style={{ display: "flex", flexDirection: "column" }}>
            <div className={styles.priceDetails}>
              <Text className="color-text-secondary-dark" strong>
                Payment History
              </Text>
              <div className={`${styles.paymentMethodScroll} mt-s`}>
                {paymentHistory.map((payment, index) => {
                  const { type, paid_on, method, last4, amount, id } = payment || {}
                  const isRefund = type?.toLowerCase() === "refund"
                  const paymentKey = id || `${type}-${method}-${last4}-${index}`
                  return (
                    <React.Fragment key={paymentKey}>
                      <Row className={index > 0 ? "mt-s" : ""}>
                        <Col xs={14}>
                          <Flex vertical>
                            <Text>
                              {type || ""} ({method || ""}
                              {last4 && ` **** ${last4}`})
                            </Text>
                            <Text style={{ color: isRefund ? "var(--color-error)" : "inherit" }}>
                              {amount === undefined ? "" : numberToCurrency(amount)}
                            </Text>
                          </Flex>
                        </Col>
                        <Col xs={10}>
                          {paid_on && (
                            <Flex vertical>
                              <Text className="color-text-secondary-dark">{isRefund ? "Refunded on" : "Paid on"}</Text>
                              <Text>{moment(paid_on).format("MM/DD/YYYY")}</Text>
                            </Flex>
                          )}
                        </Col>
                      </Row>
                      {index < paymentHistory.length - 1 && <Divider className="my-xs" />}
                    </React.Fragment>
                  )
                })}
              </div>
            </div>
          </Col>
        )}
      </Row>
    </Card>
  )
})

PaymentCard.propTypes = {
  paymentStatus: PropTypes.string,
  paymentSummary: PropTypes.shape({
    total_price: PropTypes.number,
    remaining_balance: PropTypes.number,
    adjust_price_history: PropTypes.array
  }),
  paymentHistory: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number,
      last4: PropTypes.string,
      type: PropTypes.string,
      method: PropTypes.string,
      paid_on: PropTypes.string
    })
  ),
  pricing: PropTypes.object
}

PaymentCard.displayName = "PaymentCard"

export default PaymentCard
